import { Link } from 'react-router-dom';
import { Fragment, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Title } from 'rizzui/typography';
import { Collapse } from 'rizzui/collapse';
import cn from '@core/utils/class-names';
import { PiCaretDownBold } from 'react-icons/pi';
import { useMenuItems } from '@/layouts/hydrogen/menu-items';
import StatusBadge from '@core/components/get-status-badge';

export function SidebarMenu() {
  const pathname = useLocation().pathname;
  // The template's only change: the array is the host app's permission-filtered nav, not a
  // module-level constant. Everything below is the template's markup, untouched.
  const menuItems = useMenuItems();

  // Second deliberate deviation from the template: every list module here has /module/new and
  // /module/:id sub-routes the template's own flat demo nav never anticipated, so its
  // `pathname === item.href` left the whole sidebar unhighlighted the moment you opened a
  // create or detail screen. Longest-href-wins is the same rule nav.tsx's permissionForPath
  // already uses, so a nested route highlights its own entry (e.g. "Delivery zones" under
  // /orders/delivery-zones) instead of also lighting up its parent ("Orders").
  const activeItem = menuItems
    .filter((mi): mi is typeof mi & { href: string } => typeof mi.href === 'string')
    .filter((mi) => pathname === mi.href || (mi.href !== '/' && pathname.startsWith(`${mi.href}/`)))
    .sort((a, b) => b.href.length - a.href.length)[0];

  // Third deviation (2026-09-29, project owner): a page opened from a grid or a link can sit far
  // down the nav, so the sidebar scrolls its active item into view whenever it changes — its own
  // scroll box only (the parent `overflow-y-auto` div), never the page, and only when it is out of
  // sight, so a click inside the visible nav does not jump.
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = root.current?.parentElement;
    const active = root.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!box || !active) return;
    const a = active.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (a.top < b.top || a.bottom > b.bottom) box.scrollTop += a.top - b.top - (b.height - a.height) / 2;
  }, [activeItem?.href]);

  return (
    <div ref={root} className="mt-4 pb-3 3xl:mt-6">
      {menuItems.map((item, index) => {
        const isActive = item === activeItem;
        const pathnameExistInDropdowns: any = item?.dropdownItems?.filter(
          (dropdownItem) => dropdownItem.href === pathname
        );
        const isDropdownOpen = Boolean(pathnameExistInDropdowns?.length);

        return (
          <Fragment key={item.name + '-' + index}>
            {item?.href ? (
              <>
                {item?.dropdownItems ? (
                  <Collapse
                    defaultOpen={isDropdownOpen}
                    header={({ open, toggle }) => (
                      <div
                        onClick={toggle}
                        className={cn(
                          'group relative mx-3 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 font-medium lg:my-1 2xl:mx-5 2xl:my-2',
                          isDropdownOpen
                            ? 'before:top-2/5 text-primary before:absolute before:-start-3 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary 2xl:before:-start-5'
                            : 'text-gray-700 transition-colors duration-200 hover:bg-gray-100 dark:text-gray-700/90 dark:hover:text-gray-700'
                        )}
                      >
                        <span className="flex items-center">
                          {item?.icon && (
                            <span
                              className={cn(
                                'me-2 inline-flex h-5 w-5 items-center justify-center rounded-md [&>svg]:h-[20px] [&>svg]:w-[20px]',
                                isDropdownOpen
                                  ? 'text-primary'
                                  : 'text-gray-800 dark:text-gray-500 dark:group-hover:text-gray-700'
                              )}
                            >
                              {item?.icon}
                            </span>
                          )}
                          {item.name}
                        </span>

                        <PiCaretDownBold
                          strokeWidth={3}
                          className={cn(
                            'h-3.5 w-3.5 -rotate-90 text-gray-500 transition-transform duration-200 rtl:rotate-90',
                            open && 'rotate-0 rtl:rotate-0'
                          )}
                        />
                      </div>
                    )}
                  >
                    {item?.dropdownItems?.map((dropdownItem, index) => {
                      const isChildActive =
                        pathname === (dropdownItem?.href as string);

                      return (
                        <Link
                          to={dropdownItem?.href}
                          key={dropdownItem?.name + index}
                          className={cn(
                            'mx-3.5 mb-0.5 flex items-center justify-between rounded-md px-3.5 py-2 font-medium capitalize last-of-type:mb-1 lg:last-of-type:mb-2 2xl:mx-5',
                            isChildActive
                              ? 'text-primary'
                              : 'text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900'
                          )}
                        >
                          <div className="flex items-center truncate">
                            <span
                              className={cn(
                                'me-[18px] ms-1 inline-flex h-1 w-1 rounded-full bg-current transition-all duration-200',
                                isChildActive
                                  ? 'bg-primary ring-[1px] ring-primary'
                                  : 'opacity-40'
                              )}
                            />{' '}
                            <span className="truncate">
                              {dropdownItem?.name}
                            </span>
                          </div>
                          {dropdownItem?.badge?.length ? (
                            <StatusBadge status={dropdownItem?.badge} />
                          ) : null}
                        </Link>
                      );
                    })}
                  </Collapse>
                ) : (
                  <Link
                    to={item?.href}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      // The Marketplace admin portal's nav item (2026-09-28, non-mechanical edit):
                      // 14px medium text, a 16px icon, and the active item as a tinted pill in
                      // Steelyard's own brand colour (primary) with a bar just outside its edge.
                      'group relative mx-4 my-0.5 flex items-center justify-between rounded-lg px-3.5 py-2 text-sm font-medium capitalize transition-colors 2xl:mx-5',
                      isActive
                        ? 'bg-primary/10 font-semibold text-primary shadow-sm before:absolute before:inset-y-1 before:-start-1 before:block before:w-1 before:rounded-full before:bg-current'
                        : 'text-gray-900 hover:bg-gray-100 dark:text-gray-700/90'
                    )}
                  >
                    <div className="flex items-center truncate">
                      {item?.icon && (
                        <span
                          className={cn(
                            'me-2.5 inline-flex size-4 items-center justify-center [&>svg]:size-4',
                            isActive
                              ? 'text-primary'
                              : 'text-gray-900 dark:text-gray-500 dark:group-hover:text-gray-700'
                          )}
                        >
                          {item?.icon}
                        </span>
                      )}
                      <span className="truncate">{item.name}</span>
                    </div>
                    {item?.badge?.length ? (
                      <StatusBadge status={item?.badge} />
                    ) : null}
                  </Link>
                )}
              </>
            ) : (
              <Title
                as="h6"
                className={cn(
                  'mb-2 truncate px-7 font-inter text-xs font-semibold uppercase tracking-wide text-gray-500 2xl:px-8',
                  index !== 0 && 'mt-6'
                )}
              >
                {item.name}
              </Title>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
